import type { ReactNode } from 'react';
import type { ComponentType } from 'react';

import { Countdown } from '@ui/uikit/components/common/Countdown';
import { TimeOngoing } from '@ui/uikit/components/icons/mono/TimeOngoing';
import { TimeUpcoming } from '@ui/uikit/components/icons/mono/TimeUpcoming';

import { Fire } from '../../../icons/mono/Fire';
import { MarkCheck } from '../../../icons/mono/MarkCheck';
import { MarkCheckDotted } from '../../../icons/mono/MarkCheckDotted';
import { SandClock } from '../../../icons/mono/SandClock';
import { Typography } from '../../Typography';
import styles from './Status.module.scss';

export type BadgeVariant = 'neutral' | 'highlight' | 'positive' | 'negative' | 'completed';

export type StatusType =
  | 'available'
  | 'active'
  | 'underReview'
  | 'claimed'
  | 'ended'
  | 'comingSoon'
  | 'started'
  | 'paused';

interface StatusProps {
  status: StatusType;
  spotsLeft?: number;
  endDate?: Date | null;
}

const statusConfig: Record<
  StatusType,
  {
    getCaption: (spotsLeft?: number, endDate?: Date | null) => string | ReactNode;
    icon: ComponentType<{ className?: string }>;
    iconColor: string;
    variant: BadgeVariant;
    isDisabled?: boolean;
  }
> = {
  available: {
    getCaption: (spotsLeft) => `${spotsLeft} Spots Left`,
    icon: Fire,
    iconColor: 'var(--text-color-red)',
    variant: 'neutral',
  },
  active: {
    getCaption: () => 'Active',
    icon: Fire,
    iconColor: 'var(--text-color-red)',
    variant: 'neutral',
  },
  underReview: {
    getCaption: () => 'Under Review',
    icon: MarkCheckDotted,
    iconColor: 'var(--text-color-tertiary)',
    variant: 'neutral',
    isDisabled: true,
  },
  claimed: {
    getCaption: () => 'Claimed',
    icon: MarkCheck,
    iconColor: 'var(--text-color-pink)',
    variant: 'completed',
  },
  comingSoon: {
    getCaption: () => 'Coming Soon',
    icon: TimeUpcoming,
    iconColor: 'var(--text-color-tertiary)',
    variant: 'neutral',
    isDisabled: true,
  },
  ended: {
    getCaption: () => 'Ended',
    icon: SandClock,
    iconColor: 'var(--text-color-tertiary)',
    variant: 'neutral',
    isDisabled: true,
  },
  paused: {
    getCaption: () => 'Queue Maxed',
    icon: TimeOngoing,
    iconColor: 'var(--text-color-tertiary)',
    variant: 'neutral',
    isDisabled: true,
  },
  started: {
    getCaption: (_, endDate) => <>Ends in {endDate ? <Countdown date={endDate} /> : 'TBD'}</>,
    icon: SandClock,
    iconColor: 'var(--text-color-green)',
    variant: 'neutral',
    isDisabled: true,
  },
};

const ActivityStatus = ({ status, spotsLeft, endDate }: StatusProps) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span data-variant={config.variant} className={styles['self']}>
      {Icon && (
        <span className={styles['icon']} style={{ color: config.iconColor }}>
          <Icon />
        </span>
      )}
      <Typography variant="caption-s-strong" color={config.isDisabled ? 'tertiary' : 'primary'}>
        {config.getCaption(spotsLeft, endDate)}
      </Typography>
    </span>
  );
};

export { ActivityStatus };
